import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { cn } from '@/lib/utils'

export type EventSearchSelectProps = {
  value?: string
  events: ICalendarEvent[]
  onValueChange: (value: string | undefined) => void
  placeholder?: string
}

export function EventSearchSelect({
  value,
  events,
  onValueChange,
  placeholder = 'Choose event',
}: EventSearchSelectProps) {
  const [open, setOpen] = useState(false)
  const selectedEvent = events.find((event) => String(event.id) === String(value))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate text-left">
            {selectedEvent?.title ?? placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command className="[&_[cmdk-input-wrapper]]:px-3 [&_[cmdk-input-wrapper]]:py-2 [&_[cmdk-input-wrapper]]:border-b [&_[cmdk-input-wrapper]]:border-border [&_[cmdk-input]]:text-sm [&_[cmdk-input]]:placeholder:text-muted-foreground">
          <CommandInput placeholder="Search events…" className="text-sm" />
          <CommandList>
            <CommandEmpty>No events found.</CommandEmpty>
            <CommandGroup>
              {events.map((event) => {
                const eventValue = String(event.id)
                return (
                  <CommandItem
                    key={eventValue}
                    value={`${event.title ?? ''} ${eventValue}`}
                    onSelect={() => {
                      onValueChange(eventValue)
                      setOpen(false)
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        eventValue === String(value ?? '') ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    {event.title ?? 'Untitled event'}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
